import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Send, 
  Phone, 
  MapPin, 
  Calendar, 
  Clock, 
  Car, 
  ArrowLeft,
  MoreVertical,
  User,
  MessageCircle,
  Smile,
  Paperclip
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import AudioRecorder from '../components/AudioRecorder';
import AudioPlayer from '../components/AudioPlayer';

const Chat = () => {
  const { connectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [connectDetails, setConnectDetails] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const [showQuickReplies, setShowQuickReplies] = useState(false);
  const [currentPlayingId, setCurrentPlayingId] = useState(null);

  // Standardized quick replies
  const quickReplies = [
    "Hi! I'm ready for the ride.",
    "What's the pickup location?",
    "I'll be there in 10 minutes.",
    "Can we meet at the main gate?",
  ];

  // Dummy connect details (in real app, fetch from API)
  const dummyConnects = {
    1: {
      id: 1,
      user: {
        name: 'Priya Sharma',
        rating: 4.8,
        rides: 45,
        avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face',
        phone: '+91-98765-43210',
        verified: true
      },
      ride: {
        from: 'Mumbai Central',
        to: 'Andheri West',
        date: '2024-01-15',
        time: '09:00 AM',
        price: 120,
        vehicle: 'Honda City',
        seats: 2,
        distance: '12 km',
        duration: '45 min'
      },
      connectPrice: 50,
      status: 'connected'
    },
    2: {
      id: 2,
      user: {
        name: 'Rahul Mehta',
        rating: 4.6,
        rides: 32,
        avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
        phone: '+91-98765-43211',
        verified: true
      },
      ride: {
        from: 'Bandra West',
        to: 'Powai',
        date: '2024-01-15',
        time: '08:30 AM',
        price: 150,
        vehicle: 'Maruti Swift',
        seats: 1,
        distance: '18 km',
        duration: '55 min'
      },
      connectPrice: 30,
      status: 'connected'
    }
  };

  // Auto-reply messages from the other person
  const autoReplies = [
    "Hi! Thanks for connecting. I'll be at the pickup point on time.",
    "Sure, I'll wait at the main entrance.",
  ];

  useEffect(() => {
    // Get connect details
    const connect = dummyConnects[connectId];
    if (connect) {
      setConnectDetails(connect);
      
      // Load existing messages from localStorage
      const savedMessages = localStorage.getItem(`chat_${connectId}`);
      if (savedMessages) {
        setMessages(JSON.parse(savedMessages));
      } else {
        // Initialize with welcome message
        const welcomeMessage = {
          id: Date.now(),
          text: `Hi ${user?.name || 'there'}! Thanks for connecting. I'm ${connect.user.name}. Ready for our ride from ${connect.ride.from} to ${connect.ride.to}?`,
          sender: connect.user.name,
          timestamp: new Date().toISOString(),
          isOtherUser: true
        };
        setMessages([welcomeMessage]);
        localStorage.setItem(`chat_${connectId}`, JSON.stringify([welcomeMessage]));
      }
    } else {
      navigate('/connects');
    }
  }, [connectId, user, navigate]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Handle audio recording completion
  const handleAudioRecordingComplete = (audioData) => {
    const message = {
      id: Date.now(),
      type: 'audio',
      audioUrl: audioData.url,
      duration: audioData.duration,
      sender: user?.name || 'You',
      timestamp: new Date().toISOString(),
      isOtherUser: false
    };

    const updatedMessages = [...messages, message];
    setMessages(updatedMessages);
    localStorage.setItem(`chat_${connectId}`, JSON.stringify(updatedMessages));

    // Simulate auto-reply after 2-3 seconds
    setTimeout(() => {
      const randomReply = autoReplies[Math.floor(Math.random() * autoReplies.length)];
      const autoReply = {
        id: Date.now() + 1,
        text: randomReply,
        sender: connectDetails?.user.name || 'Driver',
        timestamp: new Date().toISOString(),
        isOtherUser: true
      };
      
      const finalMessages = [...updatedMessages, autoReply];
      setMessages(finalMessages);
      localStorage.setItem(`chat_${connectId}`, JSON.stringify(finalMessages));
    }, 2000 + Math.random() * 1000);
  };

  // Handle audio play state change
  const handlePlayStateChange = (messageId, isPlaying) => {
    setCurrentPlayingId(isPlaying ? messageId : null);
  };

  const sendMessage = (text) => {
    if (!text.trim()) return;

    const message = {
      id: Date.now(),
      text: text,
      sender: user?.name || 'You',
      timestamp: new Date().toISOString(),
      isOtherUser: false
    };

    const updatedMessages = [...messages, message];
    setMessages(updatedMessages);
    localStorage.setItem(`chat_${connectId}`, JSON.stringify(updatedMessages));
    setNewMessage('');
    setShowQuickReplies(false);

    // Simulate auto-reply after 2-3 seconds
    setTimeout(() => {
      const randomReply = autoReplies[Math.floor(Math.random() * autoReplies.length)];
      const autoReply = {
        id: Date.now() + 1,
        text: randomReply,
        sender: connectDetails?.user.name || 'Driver',
        timestamp: new Date().toISOString(),
        isOtherUser: true
      };
      
      const finalMessages = [...updatedMessages, autoReply];
      setMessages(finalMessages);
      localStorage.setItem(`chat_${connectId}`, JSON.stringify(finalMessages));
    }, 2000 + Math.random() * 1000);
  };

  const handleSendMessage = () => {
    sendMessage(newMessage);
  };

  const handleQuickReply = (reply) => {
    sendMessage(reply);
  };

  const handleCall = () => {
    alert(`Calling ${connectDetails?.user.phone}`);
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  if (!connectDetails) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate('/connects')}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <img
              src={connectDetails.user.avatar}
              alt={connectDetails.user.name}
              className="w-10 h-10 rounded-full object-cover"
            />
            <div>
              <h2 className="font-semibold text-gray-900">{connectDetails.user.name}</h2>
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <span>●</span>
                <span>Online</span>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleCall}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              title="Call"
            >
              <Phone className="w-5 h-5 text-gray-600" />
            </button>
            <button className="p-2 hover:bg-gray-100 rounded-full transition-colors">
              <MoreVertical className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Ride Details */}
        <div className="px-4 pb-4">
          <div className="bg-blue-50 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <MapPin className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-gray-900">
                  {connectDetails.ride.from} → {connectDetails.ride.to}
                </span>
              </div>
              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                Connected
              </span>
            </div>
            <div className="flex items-center space-x-4 text-xs text-gray-600">
              <div className="flex items-center space-x-1">
                <Calendar className="w-3 h-3" />
                <span>{connectDetails.ride.date}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Clock className="w-3 h-3" />
                <span>{connectDetails.ride.time}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Car className="w-3 h-3" />
                <span>{connectDetails.ride.vehicle}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.isOtherUser ? 'justify-start' : 'justify-end'}`}
          >
            <div
              className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                message.isOtherUser
                  ? 'bg-white border border-gray-200'
                  : 'bg-blue-600 text-white'
              }`}
            >
              {message.type === 'audio' ? (
                <AudioPlayer
                  audioUrl={message.audioUrl}
                  duration={message.duration}
                  isOtherUser={message.isOtherUser}
                  messageId={message.id}
                  onPlayStateChange={handlePlayStateChange}
                />
              ) : (
                <p className="text-sm">{message.text}</p>
              )}
              <p className={`text-xs mt-1 ${
                message.isOtherUser ? 'text-gray-500' : 'text-blue-100'
              }`}>
                {formatTime(message.timestamp)}
              </p>
            </div>
          </div>
        ))}
        
        {isTyping && (
          <div className="flex justify-start">
            <div className="bg-white border border-gray-200 rounded-lg px-4 py-2">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Quick Replies */}
      {showQuickReplies && (
        <div className="bg-white border-t border-gray-200 p-4">
          <div className="flex flex-wrap gap-2">
            {quickReplies.map((reply, index) => (
              <button
                key={index}
                onClick={() => handleQuickReply(reply)}
                className="px-3 py-2 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-gray-200 transition-colors"
              >
                {reply}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Message Input */}
      <div className="bg-white border-t border-gray-200 p-4">
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowQuickReplies(!showQuickReplies)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            title="Quick Replies"
          >
            <Smile className="w-5 h-5 text-gray-600" />
          </button>
          <button className="p-2 hover:bg-gray-100 rounded-full transition-colors" title="Attach">
            <Paperclip className="w-5 h-5 text-gray-600" />
          </button>
          <div className="flex-1 relative">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              placeholder="Type a message..."
              className="w-full px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <button
            onClick={handleSendMessage}
            disabled={!newMessage.trim()}
            className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
        
        {/* Audio Recorder */}
        <div className="mt-2">
          <AudioRecorder
            onRecordingComplete={handleAudioRecordingComplete}
            onCancel={() => {}}
            disabled={false}
          />
        </div>
      </div>
    </div>
  );
};

export default Chat; 